import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from '../../components/navbar/index.jsx';
import Footer from '../../components/Footer/Footer.js';

/* ── Simple Aurora Background ── */
const ThankYouAurora = () => {
  useEffect(() => {
    const canvas = document.createElement('canvas');
    canvas.style.position = 'absolute';
    canvas.style.inset = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';
    canvas.style.pointerEvents = 'none';
    
    const container = document.getElementById('thankyou-canvas');
    if (container) {
      container.innerHTML = '';
      container.appendChild(canvas);
    }

    const ctx = canvas.getContext('2d')!;
    let W = canvas.width = window.innerWidth;
    let H = canvas.height = window.innerHeight;

    const resize = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resize);

    const particles = Array.from({ length: 30 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.2,
      vy: (Math.random() - 0.5) * 0.2,
      r: Math.random() * 1.2 + 0.3,
      a: Math.random() * 0.3 + 0.1,
    }));

    const blobs = [
      { x: 0.2, y: 0.3, r: 0.4, h: 195, ph: 0 },
      { x: 0.7, y: 0.6, r: 0.35, h: 210, ph: 2 },
    ];

    let t = 0;
    const draw = () => {
      t += 0.005;
      ctx.clearRect(0, 0, W, H);

      const bg = ctx.createLinearGradient(0, 0, W, H);
      bg.addColorStop(0, "#051020");
      bg.addColorStop(0.5, "#082030");
      bg.addColorStop(1, "#051020");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      blobs.forEach(b => {
        const bx = (b.x + Math.sin(t * 0.5 + b.ph) * 0.05) * W;
        const by = (b.y + Math.cos(t * 0.4 + b.ph) * 0.05) * H;
        const gr = ctx.createRadialGradient(bx, by, 0, bx, by, b.r * Math.min(W, H));
        gr.addColorStop(0, `hsla(${b.h},80%,60%,0.08)`);
        gr.addColorStop(0.5, `hsla(${b.h},70%,45%,0.04)`);
        gr.addColorStop(1, "transparent");
        ctx.fillStyle = gr;
        ctx.fillRect(0, 0, W, H);
      });

      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < 0 || p.y > H) p.vy *= -1;
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(45,212,191,${p.a})`;
        ctx.fill();
      });

      requestAnimationFrame(draw);
    };
    
    draw();
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <div id="thankyou-canvas" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />;
};

export default function ThankYou() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <div style={{ 
        fontFamily: "'Bricolage Grotesque', sans-serif", 
        background: "#020c18", 
        color: "#fff", 
        overflowX: "hidden"
      }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..60,300;12..60,400;12..60,500;12..60,600;12..60,700;12..60,800&family=Instrument+Serif:ital@0;1&display=swap');

          * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
          }

          :root {
            --teal: #14b8a6;
            --teal2: #0d9488;
            --teal3: #2dd4bf;
            --blue: #38bdf8;
            --blue2: #0ea5e9;
          }

          .thankyou-container {
            position: relative;
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 80px 5% 60px;
          }

          .thankyou-card {
            position: relative;
            z-index: 10;
            max-width: 600px;
            width: 100%;
            background: rgba(30, 53, 72, 0.75);
            backdrop-filter: blur(16px) saturate(1.8);
            border: 1px solid rgba(45, 212, 191, 0.25);
            border-radius: 32px;
            padding: 48px 40px;
            box-shadow: 0 30px 60px rgba(0, 0, 0, 0.5),
                        0 0 40px rgba(20, 184, 166, 0.15);
            animation: fadeUp 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
            text-align: center;
          }

          @keyframes fadeUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .success-icon {
            width: 80px;
            height: 80px;
            background: linear-gradient(135deg, rgba(20, 184, 166, 0.2), rgba(56, 189, 248, 0.2));
            border: 2px solid rgba(45, 212, 191, 0.5);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 28px;
            font-size: 40px;
            color: var(--teal3);
            box-shadow: 0 0 40px rgba(45, 212, 191, 0.3);
          }

          .thankyou-title {
            font-size: clamp(2rem, 4vw, 2.8rem);
            font-weight: 800;
            line-height: 1.1;
            letter-spacing: -0.02em;
            margin-bottom: 12px;
            color: #fff;
          }

          .thankyou-title span {
            font-family: 'Instrument Serif', serif;
            font-style: italic;
            color: var(--teal3);
            font-weight: 400;
          }

          .thankyou-subtitle {
            font-size: 1rem;
            color: rgba(255, 255, 255, 0.8);
            line-height: 1.6;
            margin-bottom: 32px;
            font-weight: 300;
            max-width: 440px;
            margin-left: auto;
            margin-right: auto;
          }

          .confirmation-message {
            background: rgba(45, 212, 191, 0.08);
            border: 1px solid rgba(45, 212, 191, 0.2);
            border-radius: 16px;
            padding: 20px;
            margin: 28px 0;
            font-size: 14px;
            color: rgba(255, 255, 255, 0.9);
            line-height: 1.6;
          }

          .confirmation-message strong {
            color: var(--teal3);
            font-weight: 700;
          }

          .action-buttons {
            display: flex;
            gap: 16px;
            justify-content: center;
            flex-wrap: wrap;
            margin: 32px 0 16px;
          }

          .btn-primary {
            background: linear-gradient(135deg, #0d9488, #0ea5e9);
            border: none;
            color: #fff;
            padding: 12px 32px;
            border-radius: 50px;
            font-size: 13px;
            font-weight: 700;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            transition: all 0.25s;
            box-shadow: 0 0 30px rgba(20, 184, 166, 0.3);
          }

          .btn-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 0 50px rgba(20, 184, 166, 0.5);
          }

          .btn-secondary {
            background: transparent;
            border: 1px solid rgba(255, 255, 255, 0.25);
            color: rgba(255, 255, 255, 0.9);
            padding: 12px 32px;
            border-radius: 50px;
            font-size: 13px;
            font-weight: 600;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            transition: all 0.25s;
          }

          .btn-secondary:hover {
            border-color: var(--teal3);
            color: var(--teal3);
            transform: translateY(-2px);
          }

          .reference {
            font-size: 11px;
            color: rgba(255, 255, 255, 0.3);
            margin-top: 24px;
            padding-top: 20px;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
          }

          .reference span {
            color: var(--teal3);
            background: rgba(45, 212, 191, 0.1);
            padding: 2px 10px;
            border-radius: 40px;
            margin-left: 6px;
          }

          @media (max-width: 768px) {
            .thankyou-card {
              padding: 40px 24px;
            }
          }

          @media (max-width: 480px) {
            .thankyou-card {
              padding: 32px 20px;
            }
            
            .success-icon {
              width: 64px;
              height: 64px;
              font-size: 32px;
            }
            
            .btn-primary, .btn-secondary {
              width: 100%;
              justify-content: center;
            }
          }
        `}</style>

        <ThankYouAurora />

        <div className="thankyou-container">
          <div className="thankyou-card">
            <div className="success-icon">✓</div>

            <h1 className="thankyou-title">
              Application <span>Received</span>
            </h1>

            <p className="thankyou-subtitle">
              Thank you for applying. Our team will review and reach out within 2 business days.
            </p>

            <div className="confirmation-message">
              <strong>✓ Confirmation sent to your email</strong>
              <br />
              We'll contact you for a brief introductory call if shortlisted.
            </div>

            <div className="action-buttons">
              <Link to="/" className="btn-primary">
                ← Back to Home
              </Link>
                
            </div>

            {/* <div className="reference">
              Ref: <span>AQT-{Math.random().toString(36).substring(2, 8).toUpperCase()}</span>
            </div> */}
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}